import {
  QButton,
  QModal,
  QModalBody,
  QModalHeader,
  QText,
  QModalActions,
  QStack,
  QSelect,
  QSelectPlaceholder,
} from '@qualio/ui-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateDetail } from '../../types';
import { useQualityUsers } from '../../hooks/useQualityUsers';
import { QSelectItem } from '@qualio/ui-components/lib/QAtoms/Select/types';
import { useChangeTemplateOwner } from '../../hooks/useChangeTemplateOwner';

export type ChangeTemplateOwnerProps = {
  template: TemplateDetail;
  onClose: () => void;
};

export const ChangeTemplateOwner: React.FC<ChangeTemplateOwnerProps> = ({ template, onClose }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQualityUsers();
  const [newOwnerUserId, setNewOwnerUserId] = useState<null | number>(null);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const options = useMemo(() => {
    return (data ?? [])
      .filter((u) => u.user_id !== template.owner?.user_id && u.active === true)
      .map((u) => {
        return { label: u.full_name, value: String(u.user_id) };
      });
  }, [data, template.owner?.user_id]);

  const { mutate: changeTemplateOwner, isLoading: isChangeTemplateOwnerLoading } = useChangeTemplateOwner(
    template.template_uuid,
  );

  const handleApplyChanges = useCallback(() => {
    changeTemplateOwner(newOwnerUserId!);
    onClose();
  }, [newOwnerUserId, changeTemplateOwner, onClose]);

  const handleOnChange = useCallback(
    (v: QSelectItem | null) => {
      if (v) {
        setNewOwnerUserId(Number(v.value));
      }
    },
    [setNewOwnerUserId],
  );

  return (
    <QModal isOpen={true} onClose={handleClose} size="md">
      <QModalHeader>
        <QText>{t('template.changeOwner.title')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing="16px" data-cy="change-owner-modal-body">
          <QStack spacing="8px">
            <QText weight="semibold">{t('template.changeOwner.currentOwner')}</QText>
            <QText>{template.owner_full_name ?? '-'}</QText>
          </QStack>
          <QStack spacing="8px">
            <QText weight="semibold">{t('template.changeOwner.newOwner')}</QText>
            <QSelect
              size="sm"
              options={options}
              value={String(newOwnerUserId)}
              onChange={handleOnChange}
              isLoading={isLoading}
            >
              <QSelectPlaceholder>
                <QText>{t('template.changeOwner.selectPlaceholder')}</QText>
              </QSelectPlaceholder>
            </QSelect>
          </QStack>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          variant="outline"
          data-cy="change-owner-cancel-button"
          onClick={handleClose}
          isDisabled={isChangeTemplateOwnerLoading}
        >
          {t('common.buttons.cancel')}
        </QButton>
        <QButton
          isLoading={isChangeTemplateOwnerLoading}
          isDisabled={!newOwnerUserId || isChangeTemplateOwnerLoading}
          data-cy="change-owner-save-button"
          onClick={handleApplyChanges}
        >
          {t('common.buttons.apply')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
